import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import Logo from '@lib/components/Logo/Logo';
import Trans from '@lib/components/Trans/Trans';
import Typography from '@lib/components/Typography/Typography';
import LinkComponent, { LinkSizes } from '@lib/components/Link/Link';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import { useCountdown } from '@lib/hooks/useCountdown';
import styles from './TechnicalPauseTracker.module.scss';

type Props = {
  technicalPauseEndDate: Date;
  showContactUs?: boolean;
};

export default function TechnicalPauseTracker({
  technicalPauseEndDate,
  showContactUs,
}: Props) {
  const { t } = useTranslation();
  const data = useCountdown(technicalPauseEndDate);
  const { hours, minutes, seconds, days } = data;
  useEffect(() => {
    if (hours === 0 && minutes === 0 && seconds === 0 && days === 0) {
      window.location.replace(window.location.href);
    }
  }, [days, hours, minutes, seconds]);
  return (
    <div className={styles.root}>
      <div className={styles.whiteBox}>
        <Logo className={styles.logo} />
        <Typography variant="h1" className={styles.title}>
          {t('maintenance-state-title')}
        </Typography>
        <Typography variant="caption" className={styles.text}>
          <Trans i18nKey="maintenance-state-text" />
        </Typography>
        <Typography variant="body" className={styles.subTitle}>
          {t('maintenance-state-sub-title')}:
        </Typography>
        <div className={styles.countdownSection}>
          <div className={styles.countdown}>
            {[
              { label: t('days'), value: days },
              { label: t('hours'), value: hours },
              { label: t('minutes'), value: minutes },
              { label: t('seconds'), value: seconds },
            ].map(({ label, value }) => (
              <div key={uuidv4()}>
                <div className={styles.countdownDigits}>
                  {String(value)
                    .padStart(2, '0')
                    .split('')
                    .map((digit) => (
                      <Typography
                        variant="h1"
                        key={uuidv4()}
                        className={styles.countdownDigit}
                      >
                        {digit}
                      </Typography>
                    ))}
                </div>
                <Typography variant="caption" className={styles.countdownLabel}>
                  {label}
                </Typography>
              </div>
            ))}
          </div>
        </div>
        {showContactUs && (
          <>
            <span className={styles.line} />
            <Typography variant="caption" className={styles.contactUsText}>
              {t('contact-us')}:
            </Typography>
            <div className={styles.contactUsLink}>
              <MaterialIcon
                icon="mail"
                symbolsOutlined
                size="md-16"
                className={styles.contactUsLinkIcon}
              />
              <LinkComponent
                size={LinkSizes.small}
                href="mailto:email-is-needed@datizy.com"
              >
                email-is-needed@datizy.com
              </LinkComponent>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
